import { useEffect, useRef } from 'react';
import {
  Application,
  Container,
  Graphics,
  Text,
  TextStyle,
  BlurFilter,
  type Ticker,
} from 'pixi.js';
import { AdvancedBloomFilter } from 'pixi-filters';
import { ROOMS, ROOMS_BY_ID, STATION_SIZE, CONNECTIONS } from '../../lib/rooms';
import { AGENTS } from '../../lib/agents';
import type { AgentState, AgentId } from '../../lib/schema';
import type { CameraState } from '../../hooks/useCinematicCamera';

interface Props {
  readonly agentStates: readonly AgentState[];
  readonly camera: CameraState;
  readonly onSelectAgent: (id: AgentId) => void;
}

// Monochrome cyan axis — no purple, no magenta. Amber reserved for hero only.
const STATUS_COLOR: Record<AgentState['status'], number> = {
  idle: 0x6f8aa0,
  working: 0x6fd6ff,
  waiting: 0xe8c878,
  blocked: 0xff5a5a,
  offline: 0x3d4a5c,
  celebrating: 0x80ffc8,
};

const ACCENT_HEX: Record<string, number> = {
  'var(--color-deck-amber)': 0xffcf70,   // hero / Command Deck only
  'var(--color-deck-violet)': 0x7abdff,  // now a colder cyan-blue
  'var(--color-deck-cyan)': 0x6fd6ff,    // primary cyan
  'var(--color-deck-mint)': 0x80e6d6,    // teal variant
  'var(--color-deck-magenta)': 0xff5a5a, // alert only (unused in room palette)
};

// ════════════════════════════════════════════════════════════════════
// Particle beam — animated flowing dots along a bezier (Magic UI style)
// ════════════════════════════════════════════════════════════════════

interface Beam {
  readonly path: { x: number; y: number }[];
  readonly container: Container;
  readonly particles: { gfx: Graphics; t: number; speed: number }[];
  spawnTimer: number;
}

/** Sample a cubic bezier curve into N points. */
function sampleBezier(
  x1: number, y1: number,
  cx1: number, cy1: number,
  cx2: number, cy2: number,
  x2: number, y2: number,
  steps: number,
): { x: number; y: number }[] {
  const pts: { x: number; y: number }[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const u = 1 - t;
    const x = u * u * u * x1 + 3 * u * u * t * cx1 + 3 * u * t * t * cx2 + t * t * t * x2;
    const y = u * u * u * y1 + 3 * u * u * t * cy1 + 3 * u * t * t * cy2 + t * t * t * y2;
    pts.push({ x, y });
  }
  return pts;
}

function makeBeam(
  from: { x: number; y: number },
  to: { x: number; y: number },
  color: number,
): Beam {
  const container = new Container();

  // Draw base line
  const line = new Graphics();
  line.moveTo(from.x, from.y).bezierCurveTo(
    from.x, from.y + 60,
    to.x, to.y - 60,
    to.x, to.y,
  );
  line.stroke({ color, width: 1, alpha: 0.25 });
  container.addChild(line);

  // Endpoint dots (bigger, brighter at the source/destination)
  const endDots = new Graphics();
  endDots.circle(from.x, from.y, 3).fill({ color, alpha: 0.9 });
  endDots.circle(to.x, to.y, 3).fill({ color, alpha: 0.9 });
  container.addChild(endDots);

  const path = sampleBezier(
    from.x, from.y,
    from.x, from.y + 60,
    to.x, to.y - 60,
    to.x, to.y,
    40,
  );

  return { path, container, particles: [], spawnTimer: 0 };
}

function updateBeam(beam: Beam, deltaMS: number, color: number) {
  beam.spawnTimer -= deltaMS;
  if (beam.spawnTimer <= 0) {
    // Spawn new particle
    const p = new Graphics();
    p.circle(0, 0, 2.6).fill({ color: 0xffffff, alpha: 1 });
    p.circle(0, 0, 5).fill({ color, alpha: 0.4 });
    p.filters = [new BlurFilter({ strength: 1 })];
    beam.container.addChild(p);
    beam.particles.push({ gfx: p, t: 0, speed: 0.0008 + Math.random() * 0.0003 });
    beam.spawnTimer = 900 + Math.random() * 700;
  }

  // Advance existing particles
  for (let i = beam.particles.length - 1; i >= 0; i--) {
    const particle = beam.particles[i];
    particle.t += particle.speed * deltaMS;
    if (particle.t >= 1) {
      beam.container.removeChild(particle.gfx);
      particle.gfx.destroy();
      beam.particles.splice(i, 1);
      continue;
    }
    const idx = Math.min(beam.path.length - 1, Math.floor(particle.t * (beam.path.length - 1)));
    const pt = beam.path[idx];
    particle.gfx.x = pt.x;
    particle.gfx.y = pt.y;
    // Ease-in / ease-out alpha: 0 → 1 at mid → 0
    const alpha = particle.t < 0.15
      ? particle.t / 0.15
      : particle.t > 0.85
        ? (1 - particle.t) / 0.15
        : 1;
    particle.gfx.alpha = alpha;
  }
}

// ════════════════════════════════════════════════════════════════════
// Decrypt text animator — typewriter scramble effect (react-bits style)
// ════════════════════════════════════════════════════════════════════

const DECRYPT_POOL = '!<>-_\\/[]{}—=+*^?#$%&█▒';

function decryptChar(): string {
  return DECRYPT_POOL[Math.floor(Math.random() * DECRYPT_POOL.length)];
}

/** Given progress 0..1, return a partially-scrambled version of text. */
function decryptText(text: string, progress: number): string {
  const chars = text.split('');
  const revealCount = Math.floor(progress * chars.length);
  return chars
    .map((c, i) => {
      if (c === ' ') return ' ';
      if (i < revealCount) return c;
      if (i < revealCount + 3) return decryptChar();
      return ' ';
    })
    .join('');
}

// ════════════════════════════════════════════════════════════════════
// Holographic room plate — floating glass rectangle with depth
// ════════════════════════════════════════════════════════════════════

function roomPlate(room: (typeof ROOMS)[number], accent: number): Container {
  const c = new Container();
  c.x = room.coords.x;
  c.y = room.coords.y;
  c.label = `room:${room.id}`;

  const W = room.coords.w;
  const H = room.coords.h;
  const R = 16; // corner radius

  // ── Depth shadow (below plate)
  const shadow = new Graphics();
  shadow.roundRect(8, 12, W, H, R);
  shadow.fill({ color: 0x000000, alpha: 0.45 });
  shadow.filters = [new BlurFilter({ strength: 18 })];
  c.addChild(shadow);

  // ── Outer aura (soft colored bloom beneath plate)
  const aura = new Graphics();
  aura.roundRect(-24, -24, W + 48, H + 48, R + 24);
  aura.fill({ color: accent, alpha: 0.13 });
  aura.filters = [new BlurFilter({ strength: 30 })];
  c.addChild(aura);

  // ── Glass body (two layers: frosted base + iridescent top)
  const glassBase = new Graphics();
  glassBase.roundRect(0, 0, W, H, R);
  glassBase.fill({ color: 0x0d1324, alpha: 0.68 });
  c.addChild(glassBase);

  // Diagonal light wash (top-left → bottom-right highlight)
  const sheen = new Graphics();
  sheen.roundRect(0, 0, W, H * 0.55, R);
  sheen.fill({ color: accent, alpha: 0.08 });
  c.addChild(sheen);

  const sheen2 = new Graphics();
  sheen2.roundRect(0, 0, W * 0.55, H, R);
  sheen2.fill({ color: 0xffffff, alpha: 0.025 });
  c.addChild(sheen2);

  // Bottom prismatic accent
  const prism = new Graphics();
  prism.roundRect(0, H * 0.72, W, H * 0.28, R);
  prism.fill({ color: accent, alpha: 0.04 });
  c.addChild(prism);

  // ── Thin crystal border (inner + outer)
  const outerBorder = new Graphics();
  outerBorder.roundRect(0, 0, W, H, R);
  outerBorder.stroke({ color: accent, width: 1.2, alpha: 0.55 });
  c.addChild(outerBorder);

  const innerBorder = new Graphics();
  innerBorder.roundRect(1.5, 1.5, W - 3, H - 3, R - 1.5);
  innerBorder.stroke({ color: 0xffffff, width: 0.8, alpha: 0.12 });
  c.addChild(innerBorder);

  // ── Top-edge iridescent hairline
  const topLine = new Graphics();
  topLine.moveTo(R, 0.5).lineTo(W - R, 0.5);
  topLine.stroke({ color: 0xffffff, width: 1, alpha: 0.6 });
  c.addChild(topLine);

  // ── Header label (precision sans, tracked)
  const labelStyle = new TextStyle({
    fontFamily: 'Inter Tight, Geist, system-ui, sans-serif',
    fontSize: 13,
    fontWeight: '600',
    letterSpacing: 0.8,
    fill: 0xffffff,
  });
  const label = new Text({ text: room.label, style: labelStyle });
  label.x = 20;
  label.y = 18;
  c.addChild(label);

  // ── Subtitle (mono, dim)
  const subStyle = new TextStyle({
    fontFamily: 'Geist Mono, JetBrains Mono, monospace',
    fontSize: 9,
    fill: accent,
    fontWeight: '500',
    letterSpacing: 1.2,
  });
  const sub = new Text({ text: room.subtitle.toUpperCase(), style: subStyle });
  sub.x = 20;
  sub.y = 38;
  sub.alpha = 0.8;
  c.addChild(sub);

  // ── Right-side ID badge
  const idStyle = new TextStyle({
    fontFamily: 'Geist Mono, JetBrains Mono, monospace',
    fontSize: 9,
    fill: 0xffffff,
    fontWeight: '400',
    letterSpacing: 1,
  });
  const idText = new Text({
    text: `0x${Math.floor(Math.random() * 0xffff)
      .toString(16)
      .toUpperCase()
      .padStart(4, '0')}`,
    style: idStyle,
  });
  idText.anchor.set(1, 0);
  idText.x = W - 20;
  idText.y = 20;
  idText.alpha = 0.35;
  c.addChild(idText);

  // ── Blinking status dot (top-right)
  const blinker = new Graphics();
  blinker.circle(W - 20, 42, 3).fill({ color: accent });
  blinker.label = 'blinker';
  c.addChild(blinker);

  // ── Holographic scan line — sweeps vertically inside room (animated in ticker)
  const scanLine = new Graphics();
  scanLine.rect(4, 0, W - 8, 1.2).fill({ color: accent, alpha: 0.55 });
  const scanGlow = new Graphics();
  scanGlow.rect(4, -10, W - 8, 10).fill({ color: accent, alpha: 0.14 });
  scanLine.addChild(scanGlow);
  scanLine.label = 'scan-line';
  scanLine.y = 72;
  c.addChild(scanLine);

  // ── Horizontal divider under header
  const divider = new Graphics();
  const dx0 = 20;
  const dx1 = W - 20;
  for (let x = dx0; x < dx1; x += 4) {
    divider.rect(x, 62, 1.5, 1);
  }
  divider.fill({ color: 0xffffff, alpha: 0.12 });
  c.addChild(divider);

  // ── Domain tag (top-center, mono uppercase)
  if ((room as { domain?: string }).domain) {
    const domainStyle = new TextStyle({
      fontFamily: 'Geist Mono, JetBrains Mono, monospace',
      fontSize: 8,
      fill: 0xffffff,
      fontWeight: '500',
      letterSpacing: 2.5,
    });
    const domain = new Text({
      text: (room as { domain?: string }).domain!,
      style: domainStyle,
    });
    domain.anchor.set(0.5, 0);
    domain.x = W / 2;
    domain.y = 20;
    domain.alpha = 0.38;
    c.addChild(domain);
  }

  return c;
}

// ── Hero content for Command Deck (wide short room)
function decorateCommandDeck(c: Container, W: number, _H: number, accent: number) {
  // Four KPI tiles across the width
  const tiles = [
    { label: 'AGENTS', value: '04' },
    { label: 'UPLINK', value: 'OK' },
    { label: 'CPU·MS', value: '2.4' },
    { label: 'UPTIME', value: '99.8' },
  ];
  const tileY = 74;
  const tileH = 96;
  const tileW = (W - 40 - (tiles.length - 1) * 16) / tiles.length;

  tiles.forEach((tile, i) => {
    const tileX = 20 + i * (tileW + 16);

    const tileBg = new Graphics();
    tileBg.roundRect(tileX, tileY, tileW, tileH, 6);
    tileBg.fill({ color: accent, alpha: 0.05 });
    tileBg.stroke({ color: accent, width: 0.8, alpha: 0.35 });
    c.addChild(tileBg);

    const labelStyle = new TextStyle({
      fontFamily: 'Geist Mono, JetBrains Mono, monospace',
      fontSize: 9,
      fill: 0xffffff,
      fontWeight: '500',
      letterSpacing: 1.8,
    });
    const label = new Text({ text: tile.label, style: labelStyle });
    label.x = tileX + 12;
    label.y = tileY + 10;
    label.alpha = 0.55;
    c.addChild(label);

    const valueStyle = new TextStyle({
      fontFamily: 'Inter Tight, system-ui, sans-serif',
      fontSize: 34,
      fill: 0xffffff,
      fontWeight: '500',
      letterSpacing: -0.8,
    });
    const value = new Text({ text: tile.value, style: valueStyle });
    value.x = tileX + 12;
    value.y = tileY + 28;
    c.addChild(value);

    // accent underline
    const underline = new Graphics();
    underline.rect(tileX + 12, tileY + tileH - 10, 24, 1.5);
    underline.fill({ color: accent, alpha: 0.8 });
    c.addChild(underline);
  });
}

// ── Chart content (sparkline) for analytics / factory / sales rooms
function decorateChart(c: Container, W: number, _H: number, accent: number, big: { label: string; value: string; suffix?: string }) {
  // Hero value
  const labelStyle = new TextStyle({
    fontFamily: 'Geist Mono, JetBrains Mono, monospace',
    fontSize: 9,
    fill: 0xffffff,
    fontWeight: '500',
    letterSpacing: 1.8,
  });
  const label = new Text({ text: big.label, style: labelStyle });
  label.x = 20;
  label.y = 78;
  label.alpha = 0.55;
  c.addChild(label);

  const valueStyle = new TextStyle({
    fontFamily: 'Inter Tight, system-ui, sans-serif',
    fontSize: 42,
    fill: 0xffffff,
    fontWeight: '400',
    letterSpacing: -1.2,
  });
  const value = new Text({ text: big.value, style: valueStyle });
  value.x = 20;
  value.y = 96;
  c.addChild(value);

  if (big.suffix) {
    const sufStyle = new TextStyle({
      fontFamily: 'Inter Tight, system-ui, sans-serif',
      fontSize: 13,
      fill: accent,
      fontWeight: '500',
    });
    const suf = new Text({ text: big.suffix, style: sufStyle });
    suf.x = 22 + value.width + 6;
    suf.y = 122;
    c.addChild(suf);
  }

  // Sparkline
  const sparkY = 168;
  const sparkH = 50;
  const sparkX = 20;
  const sparkW = W - 40;
  const points = 20;
  const spark = new Graphics();
  const pts: { x: number; y: number }[] = [];
  for (let i = 0; i < points; i++) {
    const px = sparkX + (i / (points - 1)) * sparkW;
    const phase = Math.sin(i * 0.7) * 0.4 + Math.sin(i * 0.3) * 0.3;
    const py = sparkY + sparkH / 2 - phase * (sparkH / 2 - 4);
    pts.push({ x: px, y: py });
  }
  // Area under curve
  spark.moveTo(pts[0].x, sparkY + sparkH);
  pts.forEach((p) => spark.lineTo(p.x, p.y));
  spark.lineTo(pts[pts.length - 1].x, sparkY + sparkH);
  spark.closePath();
  spark.fill({ color: accent, alpha: 0.15 });
  // Line on top
  const sparkLine = new Graphics();
  sparkLine.moveTo(pts[0].x, pts[0].y);
  pts.forEach((p) => sparkLine.lineTo(p.x, p.y));
  sparkLine.stroke({ color: accent, width: 1.8, alpha: 1 });
  c.addChild(spark);
  c.addChild(sparkLine);

  // End-dot
  const endDot = new Graphics();
  const last = pts[pts.length - 1];
  endDot.circle(last.x, last.y, 4).fill({ color: accent });
  endDot.circle(last.x, last.y, 4).stroke({ color: 0xffffff, width: 1, alpha: 0.8 });
  c.addChild(endDot);

  // Time axis ticks
  const axis = new Graphics();
  for (let i = 0; i < 6; i++) {
    const tx = sparkX + (i / 5) * sparkW;
    axis.rect(tx, sparkY + sparkH + 6, 1, 3);
  }
  axis.fill({ color: 0xffffff, alpha: 0.25 });
  c.addChild(axis);

  // Axis labels
  const axisStyle = new TextStyle({
    fontFamily: 'Geist Mono, JetBrains Mono, monospace',
    fontSize: 7,
    fill: 0xffffff,
    fontWeight: '400',
    letterSpacing: 1,
  });
  ['−24h', '−18h', '−12h', '−6h', 'NOW'].forEach((txt, i) => {
    const t = new Text({ text: txt, style: axisStyle });
    t.x = sparkX + (i / 4) * sparkW;
    t.y = sparkY + sparkH + 12;
    t.alpha = 0.35;
    c.addChild(t);
  });
}

// ── Dispatch decoration based on room id
function decorateRoom(c: Container, roomId: string, W: number, H: number, accent: number) {
  if (roomId === 'command-deck') {
    decorateCommandDeck(c, W, H, accent);
  } else if (roomId === 'war-room') {
    decorateChart(c, W, H, accent, { label: 'PORTFOLIO HEALTH · LIVE', value: '92%', suffix: 'ALL SYSTEMS GO' });
  } else if (roomId === 'factory-findmyfrag') {
    decorateChart(c, W, H, accent, { label: 'OFFERS RESOLVED · 24H', value: '1,428', suffix: '92% KV HIT' });
  } else if (roomId === 'sales-ops') {
    decorateChart(c, W, H, accent, { label: 'LEADS CAPTURED · WTD', value: '37', suffix: '+8 TODAY' });
  }
}

// ════════════════════════════════════════════════════════════════════
// Agent presence — energy orb with orbital particles
// ════════════════════════════════════════════════════════════════════

interface AgentPresence {
  container: Container;
  core: Graphics;
  innerGlow: Graphics;
  midGlow: Graphics;
  outerGlow: Graphics;
  orbit: Container;
  orbitDots: Graphics[];
  caption: Text;
  baseColor: number;
}

function agentPresence(color: number): AgentPresence {
  const wrap = new Container();

  // ── Outer volumetric glow (heavily blurred large disk)
  const outerGlow = new Graphics();
  outerGlow.circle(0, 0, 56).fill({ color, alpha: 0.08 });
  outerGlow.filters = [new BlurFilter({ strength: 18 })];
  wrap.addChild(outerGlow);

  // ── Mid glow
  const midGlow = new Graphics();
  midGlow.circle(0, 0, 32).fill({ color, alpha: 0.16 });
  midGlow.filters = [new BlurFilter({ strength: 10 })];
  wrap.addChild(midGlow);

  // ── Orbital ring (rotating particles)
  const orbit = new Container();
  const orbitDots: Graphics[] = [];
  const orbitRadius = 24;
  const orbitCount = 6;
  for (let i = 0; i < orbitCount; i++) {
    const theta = (i / orbitCount) * Math.PI * 2;
    const dot = new Graphics();
    dot.circle(0, 0, 1.8).fill({ color: 0xffffff, alpha: 0.85 });
    dot.x = Math.cos(theta) * orbitRadius;
    dot.y = Math.sin(theta) * orbitRadius;
    orbit.addChild(dot);
    orbitDots.push(dot);
  }
  wrap.addChild(orbit);

  // ── Inner glow (slightly crisper)
  const innerGlow = new Graphics();
  innerGlow.circle(0, 0, 14).fill({ color, alpha: 0.42 });
  innerGlow.filters = [new BlurFilter({ strength: 4 })];
  wrap.addChild(innerGlow);

  // ── Core — gradient-feel via stacked circles
  const core = new Graphics();
  // Outer ring
  core.circle(0, 0, 8).fill({ color });
  core.circle(0, 0, 8).stroke({ color: 0xffffff, width: 1.2, alpha: 0.9 });
  // Bright center
  core.circle(0, 0, 4.5).fill({ color: 0xffffff, alpha: 0.95 });
  // Tiny core highlight
  core.circle(-1.2, -1.2, 1.8).fill({ color: 0xffffff });
  wrap.addChild(core);

  // ── Caption (agent name + title, clean sans)
  const captionStyle = new TextStyle({
    fontFamily: 'Geist Mono, JetBrains Mono, monospace',
    fontSize: 10,
    fill: 0xffffff,
    fontWeight: '500',
    letterSpacing: 1.4,
  });
  const caption = new Text({ text: '', style: captionStyle });
  caption.anchor.set(0.5, 0);
  caption.y = 36;
  caption.alpha = 0.88;
  wrap.addChild(caption);

  return {
    container: wrap,
    core,
    innerGlow,
    midGlow,
    outerGlow,
    orbit,
    orbitDots,
    caption,
    baseColor: color,
  };
}

// ════════════════════════════════════════════════════════════════════
// Holographic speech caption — floating glass bubble
// ════════════════════════════════════════════════════════════════════

function speechCaption(text: string, accent: number): Container {
  const c = new Container();
  const padX = 14;
  const padY = 10;
  const style = new TextStyle({
    fontFamily: 'Geist, Inter Tight, sans-serif',
    fontSize: 13,
    fill: 0xffffff,
    letterSpacing: 0.2,
    wordWrap: true,
    wordWrapWidth: 280,
    fontWeight: '500',
  });
  const t = new Text({ text, style });
  t.x = padX;
  t.y = padY;

  const w = t.width + padX * 2;
  const h = t.height + padY * 2;

  // Outer glow
  const glow = new Graphics();
  glow.roundRect(-4, -4, w + 8, h + 8, 10);
  glow.fill({ color: accent, alpha: 0.22 });
  glow.filters = [new BlurFilter({ strength: 14 })];
  c.addChild(glow);

  // Glass body
  const bg = new Graphics();
  bg.roundRect(0, 0, w, h, 8);
  bg.fill({ color: 0x0a1020, alpha: 0.94 });
  bg.stroke({ color: 0xffffff, width: 0.8, alpha: 0.18 });
  c.addChild(bg);

  // Accent top stripe
  const stripe = new Graphics();
  stripe.roundRect(0, 0, w, 2, 8);
  stripe.fill({ color: accent, alpha: 0.85 });
  c.addChild(stripe);

  // Leading accent dot
  const dot = new Graphics();
  dot.circle(padX + 2, padY + 8, 2.5).fill({ color: accent });
  c.addChild(dot);
  t.x = padX + 12;

  c.addChild(t);
  c.pivot.set(w / 2, h + 16);
  return c;
}

// ════════════════════════════════════════════════════════════════════
// Atmospheric background — nebula fog + deep stars
// ════════════════════════════════════════════════════════════════════

function atmosphereLayer(): Container {
  const c = new Container();

  // Dot grid — tactical ops console backdrop (very subtle)
  const grid = new Graphics();
  const step = 48;
  for (let x = 0; x < STATION_SIZE.width; x += step) {
    for (let y = 0; y < STATION_SIZE.height; y += step) {
      grid.circle(x, y, 0.8).fill({ color: 0x5a7080, alpha: 0.18 });
    }
  }
  c.addChild(grid);

  // Concentric cyan rings — gives center-of-station feel
  const rings = new Graphics();
  const cx = STATION_SIZE.width / 2;
  const cy = STATION_SIZE.height / 2;
  [180, 360, 560, 800].forEach((r, i) => {
    rings.circle(cx, cy, r).stroke({ color: 0x6fd6ff, width: 1, alpha: 0.05 + i * 0.015 });
  });
  // Crosshair
  rings.moveTo(cx - 900, cy).lineTo(cx + 900, cy);
  rings.moveTo(cx, cy - 600).lineTo(cx, cy + 600);
  rings.stroke({ color: 0x6fd6ff, width: 1, alpha: 0.06 });
  c.addChild(rings);

  // Sparse deep-blue stars (not nebula — just stars)
  const stars = new Graphics();
  for (let i = 0; i < 140; i++) {
    const x = Math.random() * STATION_SIZE.width;
    const y = Math.random() * STATION_SIZE.height;
    const size = Math.random() < 0.1 ? 1.4 : 0.6;
    const a = 0.2 + Math.random() * 0.5;
    stars.circle(x, y, size).fill({ color: 0xc8d8e8, alpha: a });
  }
  c.addChild(stars);

  return c;
}

// ════════════════════════════════════════════════════════════════════
// Component
// ════════════════════════════════════════════════════════════════════

export function StationCanvas({ agentStates, camera, onSelectAgent }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const appRef = useRef<Application | null>(null);
  const presenceRef = useRef<Map<AgentId, AgentPresence>>(new Map());
  const filtersRef = useRef<{
    bloom: AdvancedBloomFilter | null;
  }>({ bloom: null });
  const lastActivityRef = useRef<Map<AgentId, string>>(new Map());
  const onSelectAgentRef = useRef(onSelectAgent);
  const agentStatesRef = useRef<readonly AgentState[]>(agentStates);
  const worldRef = useRef<Container | null>(null);

  useEffect(() => {
    onSelectAgentRef.current = onSelectAgent;
  }, [onSelectAgent]);
  useEffect(() => {
    agentStatesRef.current = agentStates;
  }, [agentStates]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let disposed = false;
    let cleanup: (() => void) | null = null;
    const app = new Application();

    (async () => {
      const startW = el.clientWidth || window.innerWidth;
      const startH = el.clientHeight || window.innerHeight;

      // Cap resolution at 1.5x to keep backbuffer under 5M pixels on 1920×1080
      // viewports. High-DPR 2x on 1920×1080 = 8.3M pixels which can exhaust
      // iGPU memory (silent transparent render).
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      try {
        await app.init({
          width: startW,
          height: startH,
          backgroundAlpha: 0,
          antialias: false, // disable MSAA — bigger backbuffer cost, marginal quality gain
          resolution: dpr,
          autoDensity: true,
          powerPreference: 'high-performance',
          preference: 'webgl',
          preserveDrawingBuffer: false,
        });
        console.log('[StationCanvas] Pixi ready', {
          w: startW,
          h: startH,
          renderer: app.renderer.type === 1 ? 'WebGL' : app.renderer.type === 2 ? 'WebGPU' : '?',
          dpr,
          backbuffer: [Math.round(startW * dpr), Math.round(startH * dpr)],
        });
      } catch (err) {
        console.error('[StationCanvas] Pixi init failed', err);
        return;
      }
      if (disposed) {
        app.destroy(true, { children: true });
        return;
      }
      appRef.current = app;
      // DEV-only: expose app on window for diagnostics
      if (import.meta.env.DEV) {
        (window as unknown as { __DECK_APP__: Application }).__DECK_APP__ = app;
      }

      const canvas = app.canvas as HTMLCanvasElement;
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.display = 'block';
      canvas.setAttribute('data-testid', 'station-canvas');
      el.appendChild(canvas);

      // World container — everything scales together
      const world = new Container();
      world.label = 'world';
      worldRef.current = world;
      app.stage.addChild(world);

      // Atmosphere (background)
      world.addChild(atmosphereLayer());

      // Animated data beams — particles flowing along bezier connections
      const beams: Beam[] = [];
      CONNECTIONS.forEach((conn) => {
        const from = ROOMS_BY_ID[conn.from];
        const to = ROOMS_BY_ID[conn.to];
        if (!from || !to) return;
        const beam = makeBeam(
          { x: from.coords.x + from.coords.w / 2, y: from.coords.y + from.coords.h },
          { x: to.coords.x + to.coords.w / 2, y: to.coords.y },
          0x6fd6ff,
        );
        world.addChild(beam.container);
        beams.push(beam);
      });

      // Rooms
      ROOMS.forEach((room) => {
        const accent = ACCENT_HEX[room.accent] ?? 0xb48dff;
        const plate = roomPlate(room, accent);
        decorateRoom(plate, room.id, room.coords.w, room.coords.h, accent);
        world.addChild(plate);
      });

      // Agents — position depends on room size
      const pm = presenceRef.current;
      pm.clear();
      AGENTS.forEach((agent) => {
        const room = ROOMS_BY_ID[agent.room];
        if (!room) return;
        const color = ACCENT_HEX[agent.color] ?? 0xb48dff;
        const p = agentPresence(color);
        // Orbs sit in the upper-right of each room
        if (room.id === 'command-deck') {
          // Command Deck is wide — orb in its own right margin
          p.container.x = room.coords.x + 44;
          p.container.y = room.coords.y + 44;
        } else {
          p.container.x = room.coords.x + room.coords.w - 48;
          p.container.y = room.coords.y + 42;
        }
        p.caption.text = `${agent.label.toUpperCase()}`;
        p.container.eventMode = 'static';
        p.container.cursor = 'pointer';
        p.container.on('pointerdown', () => onSelectAgentRef.current(agent.id));
        world.addChild(p.container);
        pm.set(agent.id, p);
      });

      // No global stage filters — `AdvancedBloomFilter` on full-screen stage
      // consumes ~16x GPU memory for render targets and can silently produce
      // transparent output on integrated GPUs. Bloom/glow is achieved via
      // per-element `BlurFilter` on smaller containers, which is reliable.
      filtersRef.current = { bloom: null };

      // Ticker — animate orbits, pulses, blinkers, beams
      let t = 0;
      const tickerFn = (ticker: Ticker) => {
        t += ticker.deltaMS * 0.001;
        const states = agentStatesRef.current;

        // Update flowing particle beams
        beams.forEach((beam) => updateBeam(beam, ticker.deltaMS, 0x6fd6ff));

        // Animate scan lines inside each room (staggered, continuous vertical sweep)
        ROOMS.forEach((room, i) => {
          const roomContainer = world.children.find(
            (c) => (c as Container).label === `room:${room.id}`,
          ) as Container | undefined;
          if (!roomContainer) return;
          const scan = roomContainer.children.find(
            (c) => (c as Graphics).label === 'scan-line',
          ) as Graphics | undefined;
          if (scan) {
            const offset = i * 0.7; // stagger per room
            const duration = 4.5; // seconds per sweep
            const phase = ((t + offset) % duration) / duration;
            scan.y = 32 + phase * (room.coords.h - 32);
            scan.alpha = phase < 0.1
              ? phase / 0.1
              : phase > 0.9
                ? (1 - phase) / 0.1
                : 1;
          }
        });

        pm.forEach((p, id) => {
          const s = states.find((a) => a.agentId === id);
          const status = s?.status ?? 'offline';

          // Rotate orbital dots
          p.orbit.rotation = t * (status === 'celebrating' ? 1.4 : 0.4);

          // Breathe / pulse
          const breathe = 1 + Math.sin(t * 1.6) * 0.04;
          const strongBreathe = 1 + Math.sin(t * 3) * 0.14;
          if (status === 'working') {
            p.innerGlow.scale.set(breathe * 1.1);
            p.midGlow.scale.set(breathe * 1.08);
            p.outerGlow.scale.set(breathe * 1.05);
          } else if (status === 'celebrating') {
            p.innerGlow.scale.set(strongBreathe);
            p.midGlow.scale.set(strongBreathe * 1.08);
            p.outerGlow.scale.set(strongBreathe * 1.12);
          } else if (status === 'offline') {
            p.innerGlow.scale.set(0.7);
            p.midGlow.scale.set(0.7);
            p.outerGlow.scale.set(0.7);
          } else {
            p.innerGlow.scale.set(breathe);
            p.midGlow.scale.set(breathe);
            p.outerGlow.scale.set(breathe);
          }
        });

        // Pulse room blinkers
        const pulse = 0.5 + Math.sin(t * 2.4) * 0.5;
        world.children.forEach((child) => {
          const c = child as Container;
          if (c.label?.startsWith('room:')) {
            const blinker = c.children.find(
              (cc) => (cc as Graphics).label === 'blinker',
            );
            if (blinker) (blinker as Graphics).alpha = 0.55 + pulse * 0.45;

            // Drift particles
            const particles = c.children.find(
              (cc) => (cc as Container).label === 'particles',
            );
            if (particles) {
              (particles as Container).children.forEach((p, i) => {
                (p as Container).y += Math.sin(t * 1.1 + i) * 0.08;
              });
            }
          }
        });
      };
      app.ticker.add(tickerFn);

      // Responsive fit
      const onResize = () => {
        if (!el.isConnected) return;
        const cw = el.clientWidth;
        const ch = el.clientHeight;
        if (cw === 0 || ch === 0) return;

        app.renderer.resize(cw, ch);

        const targetRatio = STATION_SIZE.width / STATION_SIZE.height;
        const curRatio = cw / ch;
        const scale =
          curRatio > targetRatio
            ? ch / STATION_SIZE.height
            : cw / STATION_SIZE.width;

        world.scale.set(scale);
        world.x = (cw - STATION_SIZE.width * scale) / 2;
        world.y = (ch - STATION_SIZE.height * scale) / 2;
      };
      onResize();
      const ro = new ResizeObserver(onResize);
      ro.observe(el);

      cleanup = () => {
        ro.disconnect();
        app.ticker.remove(tickerFn);
        if (app.canvas?.parentElement) {
          app.canvas.parentElement.removeChild(app.canvas);
        }
        app.destroy(true, { children: true });
      };
    })();

    return () => {
      disposed = true;
      if (cleanup) cleanup();
      appRef.current = null;
      presenceRef.current.clear();
      worldRef.current = null;
    };
  }, []);

  // Update status-reactive visuals
  useEffect(() => {
    const app = appRef.current;
    if (!app) return;
    const pm = presenceRef.current;

    agentStates.forEach((state) => {
      const p = pm.get(state.agentId);
      if (!p) return;

      const statusColor = STATUS_COLOR[state.status];

      // Recolor glows to match status
      p.innerGlow.clear();
      p.innerGlow.circle(0, 0, 14).fill({ color: statusColor, alpha: 0.5 });
      p.midGlow.clear();
      p.midGlow.circle(0, 0, 32).fill({ color: statusColor, alpha: 0.18 });
      p.outerGlow.clear();
      p.outerGlow.circle(0, 0, 56).fill({ color: statusColor, alpha: 0.1 });

      // Dim offline
      p.container.alpha = state.status === 'offline' ? 0.38 : 1;

      // Speech caption on activity change (with decrypt effect)
      const lastActivity = lastActivityRef.current.get(state.agentId);
      if (
        state.activity &&
        state.activity !== lastActivity &&
        state.status !== 'offline' &&
        lastActivity !== undefined
      ) {
        lastActivityRef.current.set(state.agentId, state.activity);
        const agent = AGENTS.find((a) => a.id === state.agentId);
        const accent = agent ? ACCENT_HEX[agent.color] ?? 0x6fd6ff : 0x6fd6ff;
        const finalText = state.activity;
        const bubble = speechCaption(finalText, accent);
        bubble.y = -64;
        bubble.alpha = 0;
        p.container.addChild(bubble);

        // Find the Text node inside the bubble to animate its content
        const bubbleText = bubble.children.find(
          (c) => c instanceof Text,
        ) as Text | undefined;

        let elapsed = 0;
        const decryptDuration = 700;
        const fade = (ticker: Ticker) => {
          elapsed += ticker.deltaMS;
          // Fade in
          if (elapsed < 400) bubble.alpha = elapsed / 400;
          else if (elapsed < 3400) bubble.alpha = 1;
          else if (elapsed < 3900) bubble.alpha = 1 - (elapsed - 3400) / 500;
          else {
            bubble.alpha = 0;
            if (bubble.parent) bubble.parent.removeChild(bubble);
            app.ticker.remove(fade);
            return;
          }
          // Decrypt animation during first 700ms
          if (bubbleText && elapsed < decryptDuration) {
            const progress = elapsed / decryptDuration;
            bubbleText.text = decryptText(`> ${finalText}`, progress);
          } else if (bubbleText && elapsed === decryptDuration) {
            bubbleText.text = `> ${finalText}`;
          }
        };
        app.ticker.add(fade);
      } else if (lastActivity === undefined) {
        lastActivityRef.current.set(state.agentId, state.activity);
      }
    });
  }, [agentStates]);

  // Camera transforms (zoom + focus)
  useEffect(() => {
    const app = appRef.current;
    const world = worldRef.current;
    if (!app || !world) return;

    const zoomMap = { wide: 1, medium: 1.2, close: 1.45 };
    const targetScale = zoomMap[camera.zoom];

    const el = containerRef.current;
    if (el) {
      const cw = el.clientWidth;
      const ch = el.clientHeight;
      const targetRatio = STATION_SIZE.width / STATION_SIZE.height;
      const curRatio = cw / ch;
      const fitScale =
        curRatio > targetRatio
          ? ch / STATION_SIZE.height
          : cw / STATION_SIZE.width;

      world.scale.set(fitScale * targetScale);
      world.x = (cw - STATION_SIZE.width * fitScale * targetScale) / 2;
      world.y = (ch - STATION_SIZE.height * fitScale * targetScale) / 2;
    }
  }, [camera]);

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
      }}
      aria-hidden="true"
    />
  );
}
